import { AxiosInstance } from 'axios'

import { getBaseUrlV4 } from '@/utils/getBaseUrlV4'

import {
  HttpStatusCode,
  ServerError,
  Success,
  UnprocessableEntityError,
} from '@/api/results'
import { Address, AddressNote } from '@/types/api/api-v3/addresses'
import { Suggestion } from '@/types/api/api-v4/suggestions'

import type {
  AddressClient,
  FormEditAddress,
  FormNewAddress,
} from '@/api/modules/addresses/addresses.types'
import type { CoordinatesOfAddress } from '@/api/modules/geo/geocode.types'

export function createAddressesService({
  axios,
  sentry,
  city,
}: {
  axios: AxiosInstance
  sentry: any
  city: string
  hostname: string
}) {
  const { VARITI_TOKEN } = useRuntimeConfig().public
  const API_URL = getBaseUrlV4(city, { withoutCity: true, withoutUuid: true })

  return {
    async deleteAddresses(addressId: number) {
      try {
        const { data } = await axios.post(`/${city}/client/delete_address/`, {
          address_id: addressId,
        })

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Не удалось удалить текущий адрес',
              data: error.response?.data,
            })
          case HttpStatusCode.INTERNAL_SERVER_ERROR:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },
    async updateAddresses(newAddress: Address) {
      try {
        const { data } = await axios.post(
          `/${city}/client/update_address/`,
          newAddress,
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Не удалось обновить текущий адрес',
              data: error.response?.data,
            })
          case HttpStatusCode.INTERNAL_SERVER_ERROR:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },
    async updateAddressNote(newAddressNote: AddressNote) {
      try {
        const { data } = await axios.post(
          `/${city}/client/update_address/notes/`,
          newAddressNote,
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Не удалось обновить текущий адрес',
              data: error.response?.data,
            })
          case HttpStatusCode.INTERNAL_SERVER_ERROR:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },
    async createAddresses(bodyRequest: {
      street: string
      house: string
      apartment: string
      porch: string
      floor: string
      intercom: string
      notes: string
    }) {
      try {
        const { data } = await axios.post(
          `/${city}/client/create_address/`,
          bodyRequest,
        )

        return new Success(data)
      } catch (error) {
        sentry.captureException(error)

        throw error
      }
    },
    async getSuggestions(bodyRequest: {
      provider: string | undefined
      state: string | undefined
      city: string
      alias: string
      search_term: string | null
      longitude: object | undefined
      latitude: object | undefined
    }) {
      try {
        const { API_BASE_URL, VARITI_TOKEN } = useRuntimeConfig().public

        const { data } = await axios.post<Suggestion[]>(
          `${API_BASE_URL}/v4/suggestions/suggestions`,
          bodyRequest,
          {
            headers: { ...getAxiosGlobalHeader({ varitiToken: VARITI_TOKEN }) },
          },
        )

        return new Success(data)
      } catch (error) {
        sentry.captureException(error)

        throw error
      }
    },

    /**
     * Получение списка адресов клиента
     */
    async fetchNewAddresses() {
      try {
        const { data } = await axios.get<Address[]>(
          `${API_URL}/address/?city_domain=${city}`,
        )

        const address = data.map((address) => {
          return { ...address }
        })

        return new Success(address)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.INTERNAL_SERVER_ERROR:
            sentry.captureException(
              `Не удалось получить новый список адресов: ${error}`,
            )
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    /**
     * Создание нового адреса
     * @param coords - координаты адреса
     * @param form - форма полей адреса
     */
    async createNewAddress(
      coords: CoordinatesOfAddress,
      {
        selectedSuggestion,
        house,
        flat,
        porch,
        intercom,
        notes,
        floor,
      }: FormNewAddress,
    ) {
      try {
        if (!selectedSuggestion) return

        const {
          locality_with_type,
          city: cityName,
          locality,
          street_with_type,
          street,
        } = selectedSuggestion

        const { data } = await axios.post<AddressClient>(
          `${API_URL}/address/create/`,
          {
            geo_point: { ...coords },
            house,
            flat,
            porch,
            floor,
            intercom,
            notes,
            city_domain: city,
            city_name: cityName,
            street: street_with_type,
            locality: locality === street ? '' : locality_with_type,
          },
          {
            headers: { ...getAxiosGlobalHeader({ varitiToken: VARITI_TOKEN }) },
          },
        )
        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Не удалось создать новый адрес',
              data: error.response?.data,
            })
          case HttpStatusCode.INTERNAL_SERVER_ERROR:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
          case HttpStatusCode.NOT_FOUND:
            sentry.captureException(error)
            return new ServerError(error.response.data.detail)
        }
        sentry.captureException(`Не удалось создать новый адрес: ${error}`)
        return new ServerError('Что-то пошло не так. Попробуйте позже')
      }
    },

    /**
     * Редактирование адреса
     * @param id - id адреса
     * @param form - форма полей редактирования
     */
    async editAddress(id: number, form: FormEditAddress) {
      try {
        const { data } = await axios.post<AddressClient>(
          `${API_URL}/address/update/`,
          { address_id: id, ...form },
          {
            headers: { ...getAxiosGlobalHeader({ varitiToken: VARITI_TOKEN }) },
          },
        )
        return new Success(data)
      } catch (error) {
        sentry.captureException(`Не удалось отредактировать адрес: ${error}`)
        return new ServerError('Что-то пошло не так. Попробуйте позже')
      }
    },

    /**
     * Редактирование комментария у адреса
     * @param id - id адреса
     * @param notes - строка комментария
     */
    async editNotesAddress(id: number, notes: string) {
      try {
        const { data } = await axios.post<AddressClient>(
          `${API_URL}/address/update_notes/`,
          { address_id: id, notes },
          {
            headers: { ...getAxiosGlobalHeader({ varitiToken: VARITI_TOKEN }) },
          },
        )
        return new Success(data)
      } catch (error) {
        sentry.captureException(
          `Не удалось отредактировать комментарий: ${error}`,
        )
        return new ServerError('Что-то пошло не так. Попробуйте позже')
      }
    },

    /**
     * Удаление адреса у клиента
     * @param addressId - id адреса
     */
    async deleteAddress(addressId: number) {
      try {
        const { data } = await axios.post(`${API_URL}/address/delete/`, {
          address_id: addressId,
        })

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Не удалось удалить текущий адрес',
              data: error.response?.data,
            })
          default:
            sentry.captureException(`Не удалось удалить адрес: ${error}`)
            return new ServerError('Что-то пошло не так')
        }
      }
    },
  }
}
